import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Badge,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Dashboard,
  Payment,
  ExitToApp,
  Notifications,
  Search,
  Settings,
  Public,
  HelpOutline,
  ContactSupport,
  Info,
  CreditCard,
  ListAlt,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, isAuthenticated, logout } = useAuth();
  const { showSuccess } = useNotification();

  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorElUser(null);
  };

  const handleLogout = () => {
    logout();
    showSuccess('Logged out successfully');
    navigate('/');
    handleUserMenuClose();
  };

  const toggleMobileDrawer = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const getDashboardPath = () => '/dashboard';

  // Primary nav items (always visible in navbar)
  const primaryNavItems = [
    { label: 'Home', path: '/', icon: null },
    { label: 'Visa Check', path: '/visa-check', icon: <Search /> },
    { label: 'Countries', path: '/countries', icon: <Public /> },
    { label: 'Pricing', path: '/pricing', icon: <CreditCard /> },
    ...(isAuthenticated ? [
      { label: 'Dashboard', path: getDashboardPath(), icon: <Dashboard /> },
      { label: 'Applications', path: '/applications', icon: <ListAlt /> },
      { label: 'Payments', path: '/payment', icon: <Payment /> },
    ] : []),
  ];

  // Secondary nav items (shown in mobile drawer and footer, not in top navbar)
  const secondaryNavItems = [
    { label: 'About', path: '/about', icon: <Info /> },
    { label: 'Help Center', path: '/help', icon: <HelpOutline /> },
    { label: 'Contact', path: '/contact', icon: <ContactSupport /> },
  ];

  // User menu items (shown in avatar dropdown)
  const userMenuItems = [
    { label: 'Profile', path: '/profile', icon: <AccountCircle /> },
    { label: 'Dashboard', path: getDashboardPath(), icon: <Dashboard /> },
    { label: 'Applications', path: '/applications', icon: <ListAlt /> },
    { label: 'Settings', path: '/settings', icon: <Settings /> },
  ];

  const navigationItems = primaryNavItems;

  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  const Logo = () => (
    <Box
      component="img"
      src="/assets/app-icon.png"
      alt="Tripnovac Visa"
      sx={{
        height: 40,
        width: 'auto',
        cursor: 'pointer',
        mr: 2,
      }}
      onClick={() => navigate('/')}
      onError={(e) => {
        (e.target as HTMLImageElement).style.display = 'none';
      }}
    />
  );

  const TextLogo = () => (
    <Typography
      variant="h6"
      component="div"
      sx={{
        flexGrow: 0,
        fontWeight: 700,
        fontSize: '1.5rem',
        background: 'linear-gradient(96.54deg, #1273E5 41.24%, #00BCEC 59.2%)',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        cursor: 'pointer',
        mr: 4,
      }}
      onClick={() => navigate('/')}
    >
      Tripnovac Visa
    </Typography>
  );

  const renderMobileDrawer = () => (
    <Drawer
      anchor="left"
      open={mobileDrawerOpen}
      onClose={toggleMobileDrawer}
      sx={{
        '& .MuiDrawer-paper': {
          width: 280,
          bgcolor: 'background.paper',
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <TextLogo />
      </Box>
      <List>
        {navigationItems.map((item) => (
          <ListItem
            button
            key={item.path}
            onClick={() => {
              navigate(item.path);
              toggleMobileDrawer();
            }}
            selected={isActivePath(item.path)}
            sx={{
              '&.Mui-selected': {
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
              },
            }}
          >
            {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
        
        <Divider sx={{ my: 1 }} />
        
        {secondaryNavItems.map((item) => (
          <ListItem
            button
            key={item.path}
            onClick={() => {
              navigate(item.path);
              toggleMobileDrawer();
            }}
            selected={isActivePath(item.path)}
          >
            {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
            <ListItemText primary={item.label} />
          </ListItem>
        ))}

        {isAuthenticated && (
          <>
            <Divider sx={{ my: 1 }} />
            {userMenuItems.map((item) => (
              <ListItem
                button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  toggleMobileDrawer();
                }}
                selected={isActivePath(item.path)}
              >
                {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
                <ListItemText primary={item.label} />
              </ListItem>
            ))}
          </>
        )}
        
        {!isAuthenticated && (
          <>
            <ListItem
              button
              onClick={() => {
                navigate('/login');
                toggleMobileDrawer();
              }}
            >
              <ListItemText primary="Login" />
            </ListItem>
            <ListItem
              button
              onClick={() => {
                navigate('/register');
                toggleMobileDrawer();
              }}
            >
              <ListItemText primary="Sign Up" />
            </ListItem>
          </>
        )}
      </List>
    </Drawer>
  );

  return (
    <>
      <AppBar 
        position="fixed" 
        sx={{ 
          bgcolor: 'rgba(3, 11, 27, 0.88)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          boxShadow: '0 4px 30px rgba(0,0,0,0.15)',
          borderBottom: '1px solid',
          borderColor: 'rgba(255,255,255,0.06)',
        }}
      >
        <Toolbar sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          {/* Mobile menu button */}
          {isMobile && (
            <IconButton
              edge="start"
              aria-label="menu"
              onClick={toggleMobileDrawer}
              sx={{ mr: 2, color: 'rgba(255,255,255,0.8)' }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 0 }}>
            <Logo />
            <TextLogo />
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 4, flexGrow: 1 }}>
              {navigationItems.map((item) => (
                <Button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  sx={{
                    mx: 0.5,
                    color: isActivePath(item.path) ? '#0EA5E9' : 'rgba(255,255,255,0.75)',
                    fontWeight: isActivePath(item.path) ? 600 : 500,
                    fontSize: '0.9rem',
                    '&:hover': {
                      bgcolor: 'rgba(14, 165, 233, 0.1)',
                      color: '#0EA5E9',
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          {/* Right side actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
            {/* Notifications (for authenticated users) */}
            {isAuthenticated && (
              <IconButton sx={{ mr: 1, color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#0EA5E9' } }}>
                <Badge badgeContent={0} color="error">
                  <Notifications />
                </Badge>
              </IconButton>
            )}

            {/* User menu or login buttons */}
            {isAuthenticated ? (
              <>
                <IconButton
                  size="large"
                  edge="end"
                  aria-label="account of current user"
                  aria-controls="user-menu"
                  aria-haspopup="true"
                  onClick={handleUserMenuOpen}
                  sx={{ color: 'rgba(255,255,255,0.8)' }}
                >
                  <Avatar
                    sx={{ 
                      width: 32, 
                      height: 32, 
                      bgcolor: 'primary.main',
                      fontSize: '0.9rem',
                    }}
                  >
                    {user?.fullName?.charAt(0)?.toUpperCase() || 'U'}
                  </Avatar>
                </IconButton>
                <Menu
                  id="user-menu"
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleUserMenuClose}
                >
                  {userMenuItems.map((item) => (
                    <MenuItem key={item.path} onClick={() => { navigate(item.path); handleUserMenuClose(); }}>
                      {React.cloneElement(item.icon as React.ReactElement, { sx: { mr: 1 } })} {item.label}
                    </MenuItem>
                  ))}
                  <MenuItem onClick={handleLogout}>
                    <ExitToApp sx={{ mr: 1 }} /> Logout
                  </MenuItem>
                </Menu>
              </>
            ) : (
              !isMobile && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/login')}
                    sx={{
                      mr: 1,
                      borderColor: 'rgba(255,255,255,0.2)',
                      color: 'rgba(255,255,255,0.85)',
                      fontWeight: 500,
                      '&:hover': { borderColor: '#0EA5E9', color: '#0EA5E9', bgcolor: 'rgba(14,165,233,0.08)' },
                    }}
                  >
                    Login
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/register')}
                    sx={{
                      background: 'linear-gradient(135deg, #0EA5E9 0%, #14B8A6 100%)',
                      fontWeight: 600,
                      boxShadow: '0 4px 16px rgba(14,165,233,0.3)',
                      '&:hover': { background: 'linear-gradient(135deg, #0284C7 0%, #0D9488 100%)', boxShadow: '0 8px 24px rgba(14,165,233,0.4)' },
                    }}
                  >
                    Sign Up
                  </Button>
                </Box>
              )
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      {renderMobileDrawer()}
    </>
  );
};

export default Header;
